import React, { useState, useMemo } from 'react';
import { Ticket, CheckCircle, ArrowUp, PlusCircle, Users, UserPlus } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DETAILED_DATETIME_FORMAT } from 'constants/dates';
import dayjs from 'utils/extendedDayJS';
import {
  ActionButton,
  ActionButtonIcon,
  ActionButtonLabel,
  ActionButtonCaption
} from '@epoch-ui/components/ActionButton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@epoch-ui/components/Tooltip';
import { useEditableEvent } from 'pages/dashboard/overview/events/editor/hooks/useEditableEvent';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { cn } from '@epoch-ui/lib/utils';
import { updateCapacity } from 'pages/dashboard/overview/events/editor/api/update-capacity';
import { CapacityModalFormValues } from 'pages/dashboard/overview/events/schemas';
import {
  CapacityModal,
  TargetAttendeesModal,
  TargetAttendeesModalFormValues
} from 'pages/dashboard/overview/events/components';
import { CapacityType, EventType, GuestListPrivacyEnum } from 'types/event.types';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { PLUS_ONE_GUESTS } from 'constants/featureFlags';
import { updateTargetAttendance } from 'pages/dashboard/overview/events/editor/api/update-target-attendance';
import { updateGuestListPrivacy } from 'pages/dashboard/overview/events/editor/api/update-guest-list-privacy';
import { updatePlusOneConfig } from 'pages/dashboard/overview/events/editor/api/update-plus-one-config';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useEditSelfCheckinModal } from '@event-editor/hooks/eventEditorState';
import { useSessionsQuery } from '@event-editor/hooks/useSessionsQuery';
import {
  PlusOneConfigModal,
  PlusOneConfigFormValues
} from 'components/dashboard/EditEvent/components/PlusOneConfigModal';

import { GuestListPrivacyModal, RegistrationCutoffModal } from '../components';

export const RegistrationSettingsSection = (): React.ReactElement => {
  const [isRegistrationCutoffModalOpen, setIsRegistrationCutoffModalOpen] = useState(false);
  const [isCapacityModalOpen, setIsCapacityModalOpen] = useState(false);
  const [isTargetAttendeesModalOpen, setIsTargetAttendeesModalOpen] = useState(false);
  const [isGuestListPrivacyModalOpen, setIsGuestListPrivacyModalOpen] = useState(false);
  const [isPlusOneModalOpen, setIsPlusOneModalOpen] = useState(false);
  const isPlusOneGuestsEnabled = useFeatureFlag(PLUS_ONE_GUESTS);
  const event = useEditableEvent();
  const sessionsQuery = useSessionsQuery(event);
  const { open: openEditSelfCheckinModal } = useEditSelfCheckinModal();
  const { showToast } = useToaster();
  const queryClient = useQueryClient();

  const { mutate: mutateCapacity, isPending: isUpdatingCapacity } = useMutation({
    mutationFn: updateCapacity,
    onSuccess: async (data) => {
      if (data.updateEventCapacity?.ok) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) }),
          queryClient.invalidateQueries({
            queryKey: eventEditorQueryKeys.eventGuestStats(event.id!)
          })
        ]);
        setIsCapacityModalOpen(false);
      }
    },
    onError: () => {
      showToast('Capacity could not be updated.', { variant: ToasterVariant.ERROR });
    }
  });

  const { mutate: mutateTargetAttendance, isPending: isUpdatingTargetAttendance } = useMutation({
    mutationFn: updateTargetAttendance,
    onSuccess: (data) => {
      if (data.updateTargetAttendance?.ok) {
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        setIsTargetAttendeesModalOpen(false);
      }
    },
    onError: () => {
      showToast('Target attendance could not be updated.', { variant: ToasterVariant.ERROR });
    }
  });

  const { mutate: mutateGuestListPrivacy, isPending: isUpdatingGuestListPrivacy } = useMutation({
    mutationFn: updateGuestListPrivacy,
    onSuccess: (data) => {
      if (data.updateGuestListPrivacy?.ok) {
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        setIsGuestListPrivacyModalOpen(false);
      }
    },
    onError: () => {
      showToast('Guest list privacy could not be updated.', { variant: ToasterVariant.ERROR });
    }
  });

  const { mutate: mutatePlusOneConfig, isPending: isUpdatingPlusOneConfig } = useMutation({
    mutationFn: updatePlusOneConfig,
    onSuccess: (data) => {
      if (data.updatePlusOneConfig?.ok) {
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        setIsPlusOneModalOpen(false);
      }
    },
    onError: () => {
      showToast('Plus one configuration could not be updated.', { variant: ToasterVariant.ERROR });
    }
  });

  const isPending =
    isUpdatingCapacity ||
    isUpdatingTargetAttendance ||
    isUpdatingGuestListPrivacy ||
    isUpdatingPlusOneConfig ||
    false;

  const handleCapacitySubmit = (values: CapacityModalFormValues): void => {
    if (!event?.id) return;
    mutateCapacity({
      input: {
        eventId: event.id!,
        maxAttendees: values.maxAttendees ? Number(values.maxAttendees) : null
      }
    });
  };

  const handleTargetAttendeesSubmit = (values: TargetAttendeesModalFormValues): void => {
    if (!event?.id) return;
    mutateTargetAttendance({
      input: {
        eventId: event.id,
        targetAttendance: values.targetAttendance ? Number(values.targetAttendance) : null
      }
    });
  };

  const handleGuestListPrivacySave = (value?: GuestListPrivacyEnum): void => {
    if (!event?.id || !value) return;

    mutateGuestListPrivacy({
      input: {
        eventId: event.id,
        guestListPrivacy: value
      }
    });
  };

  const handlePlusOneConfigSubmit = (values: PlusOneConfigFormValues): void => {
    if (!event?.id) return;
    mutatePlusOneConfig({
      input: {
        eventId: event.id,
        allowPlusOnes: values.allowPlusOnes,
        maxPlusOnes: values.maxPlusOnes,
        collectQuestionnaireResponses: values.collectQuestionnaireResponses,
        ...(values.sendEmailNotification !== undefined && {
          sendEmailNotification: values.sendEmailNotification
        }),
        ...(values.sendSlackNotification !== undefined && {
          sendSlackNotification: values.sendSlackNotification
        })
      }
    });
  };

  const isLimitedCapacity = useMemo(
    () => event?.maxAttendees && event.maxAttendees > 0,
    [event?.maxAttendees]
  );

  const isCancelled = !!event.scheduledDeletionAt;
  const isEventInPast = event.hasEnded;
  const isMultiSession = event.eventType === EventType.MULTI_SESSIONS;
  const isRegistrationClosed = useMemo(
    () => isEventInPast || event?.hasEnded || !!event?.isPastRegistrationCutoff,
    [isEventInPast, event?.hasEnded, event?.isPastRegistrationCutoff]
  );

  const isEmptyMultiSession = useMemo(() => {
    return isMultiSession && sessionsQuery.data?.length === 0;
  }, [sessionsQuery.data, isMultiSession]);

  const registrationCutoffText = useMemo((): string => {
    if (!event?.registrationCutoffAt) {
      return isEventInPast ? 'Closed' : 'Open';
    }

    const timezone = event.timezoneCode || dayjs.tz.guess();
    const cutoffDate = dayjs.utc(event.registrationCutoffAt).tz(timezone);

    if (isEventInPast || dayjs().isAfter(cutoffDate)) {
      return `Closed on ${cutoffDate.format(DETAILED_DATETIME_FORMAT)}`;
    }

    return `Open until ${cutoffDate.format(DETAILED_DATETIME_FORMAT)}`;
  }, [event?.registrationCutoffAt, event?.timezoneCode, isEventInPast]);

  const guestListPrivacyText = useMemo((): string => {
    if (event?.guestListPrivacy === GuestListPrivacyEnum.HIDDEN) {
      return 'Hidden';
    }
    if (event?.isPrivate) {
      return 'Visible to guests';
    }
    return 'Visible to everyone';
  }, [event?.guestListPrivacy, event?.isPrivate]);

  const selfCheckInText = useMemo((): string => {
    const timezone = event.timezoneCode || dayjs.tz.guess();
    const eventEnd = dayjs.utc(event.datetimeEnd).tz(timezone);
    const selfCheckinEndDate = eventEnd.add(30, 'day');

    if (!event?.allowUserCheckin) {
      return 'Off';
    }

    if (event.selfCheckinSchedule) {
      const { quantity, unit, beforeEventBegins } = event.selfCheckinSchedule;
      if (quantity && unit) {
        const eventStart = dayjs.utc(event.datetimeScheduled).tz(timezone);
        const unitInLowerCase = unit.toLowerCase().slice(0, -1) as dayjs.ManipulateType;
        const checkinStartTime = beforeEventBegins
          ? eventStart.subtract(quantity, unitInLowerCase)
          : eventStart.add(quantity, unitInLowerCase);

        if (dayjs().tz(timezone).isBefore(checkinStartTime)) {
          return `Opens on ${checkinStartTime.format(DETAILED_DATETIME_FORMAT)}`;
        }
      }
    }

    if (isEventInPast) {
      return `Closed on ${selfCheckinEndDate.format(DETAILED_DATETIME_FORMAT)}`;
    }

    return `Closes on ${selfCheckinEndDate.format(DETAILED_DATETIME_FORMAT)}`;
  }, [
    event?.allowUserCheckin,
    event?.selfCheckinSchedule,
    event?.datetimeScheduled,
    event?.datetimeEnd,
    event?.timezoneCode,
    isEventInPast
  ]);

  const capacityText = useMemo(
    () => (isLimitedCapacity ? String(event.maxAttendees) : 'Unlimited'),
    [isLimitedCapacity, event?.maxAttendees]
  );

  const targetAttendeesText = useMemo(
    () => (event?.targetAttendance != null ? String(event.targetAttendance) : 'No target set'),
    [event?.targetAttendance]
  );

  const plusOneText = event.plusOneConfig?.allowPlusOnes ? 'On' : 'Off';

  const hasRegisteredGuests =
    (event.guestListTotals?.confirmed ?? 0) +
      (event.guestListTotals?.waitlisted ?? 0) +
      (event.guestListTotals?.incomplete ?? 0) >
    0;

  const hasRequiredPreEventQuestions = event.preEventQuestions?.some((q) => q.isRequired) ?? false;

  return (
    <>
      <div className="tw-mb-6 tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <ActionButton
                onClick={() => setIsRegistrationCutoffModalOpen(true)}
                disabled={event?.hasEnded || isEventInPast || isCancelled || isEmptyMultiSession}
                className="tw-rounded-3xl"
              >
                <ActionButtonIcon
                  theme="success"
                  className={cn(
                    isRegistrationClosed && 'tw-bg-negative-red-light tw-text-negative-foreground'
                  )}
                >
                  <Ticket size={24} />
                </ActionButtonIcon>

                <ActionButtonLabel>
                  Registration
                  <ActionButtonCaption>{registrationCutoffText}</ActionButtonCaption>
                </ActionButtonLabel>
              </ActionButton>
            </span>
          </TooltipTrigger>
          {isEmptyMultiSession && (
            <TooltipContent className="tw-rounded-2xl">
              Add a session to configure registration settings.
            </TooltipContent>
          )}
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <ActionButton
                className="tw-rounded-3xl"
                onClick={openEditSelfCheckinModal}
                disabled={
                  isCancelled ||
                  isPending ||
                  isEmptyMultiSession ||
                  event?.isThirtyDaysAfterEventEnded ||
                  (isEventInPast && !event?.allowUserCheckin)
                }
              >
                <ActionButtonIcon theme="success">
                  <CheckCircle size={24} />
                </ActionButtonIcon>
                <ActionButtonLabel>
                  Self check-in
                  <ActionButtonCaption>{selfCheckInText}</ActionButtonCaption>
                </ActionButtonLabel>
              </ActionButton>
            </span>
          </TooltipTrigger>
          {isEmptyMultiSession && (
            <TooltipContent className="tw-rounded-2xl">
              Add a session to schedule self check-in.
            </TooltipContent>
          )}
        </Tooltip>

        <ActionButton
          className="tw-rounded-3xl"
          disabled={isEventInPast || isCancelled || isPending}
          onClick={() => setIsGuestListPrivacyModalOpen(true)}
        >
          <ActionButtonIcon theme="purple">
            <Users size={24} />
          </ActionButtonIcon>
          <ActionButtonLabel>
            Guest list privacy
            <ActionButtonCaption>{guestListPrivacyText}</ActionButtonCaption>
          </ActionButtonLabel>
        </ActionButton>

        {!isMultiSession && (
          <>
            <ActionButton
              className="tw-rounded-3xl"
              disabled={isEventInPast || isCancelled || isPending}
              onClick={() => setIsCapacityModalOpen(true)}
            >
              <ActionButtonIcon className="tw-bg-brand-accent">
                <ArrowUp size={24} />
              </ActionButtonIcon>
              <ActionButtonLabel>
                Capacity
                <ActionButtonCaption>{capacityText}</ActionButtonCaption>
              </ActionButtonLabel>
            </ActionButton>

            <ActionButton
              className="tw-rounded-3xl"
              disabled={isEventInPast || isCancelled || isPending}
              onClick={() => setIsTargetAttendeesModalOpen(true)}
            >
              <ActionButtonIcon className="tw-bg-brand-accent">
                <PlusCircle size={24} />
              </ActionButtonIcon>
              <ActionButtonLabel>
                Target attendees
                <ActionButtonCaption>{targetAttendeesText}</ActionButtonCaption>
              </ActionButtonLabel>
            </ActionButton>

            {isPlusOneGuestsEnabled && (
              <ActionButton
                className="tw-rounded-3xl"
                disabled={isEventInPast || isCancelled || isPending}
                onClick={() => setIsPlusOneModalOpen(true)}
              >
                <ActionButtonIcon className="tw-bg-brand-accent">
                  <UserPlus size={24} />
                </ActionButtonIcon>
                <ActionButtonLabel>
                  Add +1 guests
                  <ActionButtonCaption>{plusOneText}</ActionButtonCaption>
                </ActionButtonLabel>
              </ActionButton>
            )}
          </>
        )}
      </div>

      <RegistrationCutoffModal
        open={isRegistrationCutoffModalOpen}
        onOpenChange={setIsRegistrationCutoffModalOpen}
      />
      {!isMultiSession && (
        <>
          <CapacityModal
            open={isCapacityModalOpen}
            onOpenChange={setIsCapacityModalOpen}
            initialValues={{
              maxAttendees: isLimitedCapacity ? event.maxAttendees : undefined,
              capacityType: isLimitedCapacity ? CapacityType.LIMITED : CapacityType.UNLIMITED
            }}
            onSubmit={handleCapacitySubmit}
          />

          <TargetAttendeesModal
            open={isTargetAttendeesModalOpen}
            onOpenChange={setIsTargetAttendeesModalOpen}
            initialValues={{
              targetAttendance: event?.targetAttendance ? Number(event.targetAttendance) : undefined
            }}
            onSubmit={handleTargetAttendeesSubmit}
          />

          <PlusOneConfigModal
            open={isPlusOneModalOpen}
            onOpenChange={setIsPlusOneModalOpen}
            initialValues={event.plusOneConfig ?? {}}
            onSubmit={handlePlusOneConfigSubmit}
            isPublished={event.isPublished ?? false}
            hasRegisteredGuests={hasRegisteredGuests}
            hasRequiredPreEventQuestions={hasRequiredPreEventQuestions}
          />
        </>
      )}
      <GuestListPrivacyModal
        open={isGuestListPrivacyModalOpen}
        onSave={handleGuestListPrivacySave}
        initialValue={event?.guestListPrivacy ?? undefined}
        onOpenChange={(open) => !open && setIsGuestListPrivacyModalOpen(false)}
      />
    </>
  );
};
